import { Settings } from "./types";
import { toUiCollType } from "src/utils/converting";

const defaultState = {
    aoSettings: {},
    availabilitySettings: {},
    emailSettings: {
        lenderEmails: [],
        borrowerEmails: [],
    },
    confirmationFileFields: [],
    // Backend-sourced lists, written by the settings load and by the
    // CPTY_SETTINGS_UPDATED push. Everything that acts on a list reads this, so a
    // list appears here once the backend has it and not before.
    listSettings: [],
    // Drafts the operator has added in the list-settings dialog and the backend
    // has not confirmed. They render in that dialog's table so Add shows the new
    // list straight away, and they are wiped when the dialog closes, so a draft
    // never reaches the offer grid or survives to the next open.
    pendingListSettings: [],
    dynamicAndClientPoolList: [],
    deletedListSettings: [],
    fundSettings: [],
    deletedFundSettings: [],
    userSettings: {},
    peerCptySettings: [], // [{"cptyCode": "string", "cptyName"; "string", isUnregisteredCpty: boolean, "defaultObjective": "string", emails: ["string"]}]
    featureFlags: {},
    selectedPeerCptySetting: {},
    newPeerCptySettings: [],
};

export default (state = defaultState, action) => {
    switch (action.type) {
        case Settings.UPDATE_LENDER_SETTINGS:
            const {
                autoOfferSettings,
                defaultEmails,
                availabilitySettings,
                ksdAccountSettings,
                ksfcAccountSettings,
                generalAccountSettings,
                confirmationFileFields,
                blockedSecurities,
            } = action.settings;

            const accountsByCode = (generalAccountSettings ?? []).reduce(
                (result, account) => ({
                    ...result,
                    [account.accountNo]: account,
                }),
                {}
            );
            return {
                ...state,
                emailSettings: {
                    ...state.emailSettings,
                    lenderEmails: defaultEmails ? [...defaultEmails] : [],
                },
                listSettings: action.settings.listSettings
                    ? action.settings.listSettings
                          .filter((l) => !l.orderId && !l.isDynamicList && !l.isClientPoolList)
                          .map((setting) => ({
                              ...setting,
                              ...toUiCollType(setting.collateralType),
                              ksdAccount:
                                  accountsByCode[setting.ksdAccount] ?? {},
                              ksfcAccount:
                                  accountsByCode[setting.ksfcAccount] ?? {},
                          }))
                    : [],
                dynamicAndClientPoolList:
                    action.settings.listSettings?.filter(
                        (l) => l.orderId || l.isDynamicList || l.isClientPoolList
                    ) || [],
                deletedListSettings: [],
                aoSettings: { ...autoOfferSettings },
                ksdAccountSettings: { ...ksdAccountSettings },
                ksfcAccountSettings: { ...ksfcAccountSettings },
                generalAccountSettings: [...(generalAccountSettings ?? [])],
                availabilitySettings: { ...availabilitySettings },
                confirmationFileFields: [...(confirmationFileFields ?? [])],
                blockedSecurities: blockedSecurities,
            };

        case Settings.UPDATE_BORROWER_SETTINGS:
            const { generalAccountSettings: accounts } = action.settings;
            const accountsByCodeForB = (accounts ?? []).reduce(
                (result, account) => ({
                    ...result,
                    [account.accountNo]: account,
                }),
                {}
            );

            return {
                ...state,
                ksdAccountSettings: { ...action.settings.ksdAccountSettings },
                ksfcAccountSettings: { ...action.settings.ksfcAccountSettings },
                generalAccountSettings: [...(accounts ?? [])],
                fundSettings: action.settings.fundSettings
                    ? action.settings.fundSettings.map((s) => ({
                          ...s,
                          ksdAccount: accountsByCodeForB[s.ksdAccount] ?? {},
                          ksfcAccount: accountsByCodeForB[s.ksfcAccount] ?? {},
                      }))
                    : [],
                deletedFundSettings: [],
                emailSettings: {
                    ...state.emailSettings,
                    borrowerEmails: action.settings.defaultEmails
                        ? [...action.settings.defaultEmails]
                        : [],
                },
                confirmationFileFields: [
                    ...(action.settings.confirmationFileFields ?? []),
                ],
            };

        case Settings.UPDATE_AO_SETTINGS:
            return {
                ...state,
                aoSettings: action.settings,
            };

        case Settings.UPDATE_ACCOUNT_SETTINGS:
            if (action.accountType === "KSD") {
                return {
                    ...state,
                    ksdAccountSettings: action.settings,
                };
            }
            return {
                ...state,
                ksfcAccountSettings: action.settings,
            };

        case Settings.UPDATE_EMAIL_SETTINGS:
            return {
                ...state,
                emailSettings: action.settings,
            };

        case Settings.UPDATE_CONFIRMATION_FILE_FIELDS:
            return {
                ...state,
                confirmationFileFields: action.fields,
            };
        case Settings.UPDATE_PEER_CPTY_SETTINGS:
            return {
                ...state,
                peerCptySettings: [...action.settings],
            };

        case Settings.ADD_PENDING_LIST_SETTINGS:
            return {
                ...state,
                pendingListSettings: [
                    ...state.pendingListSettings,
                    action.newSetting,
                ],
                deletedListSettings: state.deletedListSettings.filter(
                    (ls) => ls !== action.newSetting.name
                ),
            };

        case Settings.CLEAR_PENDING_LIST_SETTINGS:
            return {
                ...state,
                pendingListSettings: [],
            };

        case Settings.ADD_PENDING_BLOCKED_SECURITY:
            const pendingBlockedSecurities = [
                ...(state.pendingBlockedSecurities ?? []),
            ];

            pendingBlockedSecurities.push({
                ...action.stock,
                isNew: true,
            });
            return {
                ...state,
                pendingBlockedSecurities,
            };

        case Settings.SET_PENDING_BLOCKED_SECURITY:
            return {
                ...state,
                pendingBlockedSecurities: action.stocksList,
            };

        case Settings.REMOVE_PENDING_BLOCKED_SECURITY: {
            const pendingBlockedSecurities = (
                state.pendingBlockedSecurities ?? []
            ).filter((item) => item.id !== action.securityId);

            return {
                ...state,
                pendingBlockedSecurities: pendingBlockedSecurities,
            };
        }

        case Settings.UPDATE_LIST_SETTINGS:
            // The dialog's table edits confirmed lists and drafts alike, so an
            // edit lands wherever the row lives.
            return {
                ...state,
                listSettings: state.listSettings.map((l) =>
                    l.name === action.listName
                        ? { ...l, ...action.updates }
                        : { ...l }
                ),
                pendingListSettings: state.pendingListSettings.map((l) =>
                    l.name === action.listName
                        ? { ...l, ...action.updates }
                        : { ...l }
                ),
            };

        case Settings.SET_DELETED_LIST:
            return {
                ...state,
                deletedListSettings: [...action.list],
            };

        case Settings.REMOVE_LIST_SETTINGS:
            const deletedList = state.listSettings.some(
                (l) => !l.isNew && l.name === action.name
            );
            // Removing a draft drops it from the pending set; removing a
            // confirmed list also records the deletion for the save payload.
            const result = {
                ...state,
                listSettings: state.listSettings.filter(
                    (l) => l.name !== action.name
                ),
                pendingListSettings: state.pendingListSettings.filter(
                    (l) => l.name !== action.name
                ),
            };
            if (deletedList) {
                result.deletedListSettings = [
                    ...state.deletedListSettings,
                    action.name,
                ];
            }
            return result;
        case Settings.SET_LIST_SETTINGS:
            return {
                ...state,
                listSettings: [...action.listSettings],
            };

        case Settings.DISPLAY_NEW_LIST_ERRORS:
            return {
                ...state,
                displayListErrors: action.value,
            };

        case Settings.ADD_FUND_SETTINGS:
            const fundSettings = [...state.fundSettings];
            fundSettings.push(action.newSetting);
            return {
                ...state,
                fundSettings,
                deletedFundSettings: state.deletedFundSettings.filter(
                    (ls) => ls !== action.newSetting.fundName
                ),
            };

        case Settings.SET_DELETED_FUND:
            return {
                ...state,
                deletedFundSettings: [...action.list],
            };

        case Settings.REMOVE_FUND_SETTINGS:
            const fundDeleted = state.fundSettings.some(
                (l) => !l.isNew && l.fundName === action.fundName
            );
            const newResult = {
                ...state,
                fundSettings: state.fundSettings.filter(
                    (l) => l.fundName !== action.fundName
                ),
            };
            if (fundDeleted) {
                newResult.deletedFundSettings = [
                    ...state.deletedFundSettings,
                    action.fundName,
                ];
            }
            return newResult;
        case Settings.SET_FUND_SETTINGS:
            return {
                ...state,
                fundSettings: [...action.fundSettings],
            };
        case Settings.DISPLAY_NEW_FUND_ERRORS:
            return {
                ...state,
                displayFundErrors: action.value,
            };

        case Settings.UPDATE_USER_SETTINGS:
            return {
                ...state,
                userSettings: {
                    ...action.userSettings,
                },
            };

        case Settings.SET_LD_FEATURE_FLAGS:
            return {
                ...state,
                featureFlags: { ...action.flags },
            };

        case Settings.UPDATE_LD_FEATURE_FLAGS:
            return {
                ...state,
                featureFlags: { ...state.featureFlags, ...action.flagChanges },
            };
        case Settings.SET_SELECTED_PEER_CPTY_SETTING:
            return {
                ...state,
                selectedPeerCptySetting: action.payload,
            };
        case Settings.SET_NEW_PEER_CPTY_SETTING:
            return {
                ...state,
                newPeerCptySettings: action.payload,
            };
        case Settings.UPDATE_CPTY_SECRET_KEY:
            const { name, secret } = action.payload;
            const updatedPeerCptySettings = state.peerCptySettings.map((item) =>
                item.cptyName === name ? { ...item, secret: secret } : item
            );

            return {
                ...state,
                peerCptySettings: updatedPeerCptySettings,
            };

        case Settings.SET_GENERAL_ACCOUNT_SETTING:
            return {
                ...state,
                generalAccountSettings: [...action.accounts],
            };

        case Settings.RESET_SETTINGS:
            return defaultState;

        default:
            return state;
    }
};
